import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import ScreenRecorder from '../services/screen-recorder';
import {
  CaptureInfo,
  ScreenRecorderContext,
  ScreenRecorderContextType,
} from './screen-recorder-context';

interface ScreenRecorderProviderProps {
  children: ReactNode;
}

const getOptimalRecordingSettings = () => {
  const baseSettings = {
    chunkDuration: 1000, // 1 second chunks to match camera recording
    preferredMimeTypes: [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4',
    ],
    videoBitsPerSecond: 200_000,
    audioBitsPerSecond: 32_000,
  };

  return baseSettings;
};

export const ScreenRecorderProvider: React.FC<ScreenRecorderProviderProps> = ({
  children,
}) => {
  const [screenRecorder, setScreenRecorder] = useState<ScreenRecorder | null>(
    null,
  );
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [isScreenSharingActive, setIsScreenSharingActive] = useState(false);
  const [captureInfo, setCaptureInfo] = useState<CaptureInfo | null>(null);

  // Initialize the screen recorder once
  useEffect(() => {
    const recorder = new ScreenRecorder();

    recorder.onCaptureTypeDetected = (info: CaptureInfo) => {
      setCaptureInfo(info);
      console.log('Capture type detected:', info);
    };

    setScreenRecorder(recorder);

    return () => {
      recorder.destroy();
      setScreenRecorder(null);
    };
  }, []);

  const acquireScreenStream = useCallback(async () => {
    if (!screenRecorder || isScreenSharingActive) {
      return;
    }

    try {
      const stream = await screenRecorder.acquireStream();
      setScreenStream(stream);
      setIsScreenSharingActive(true);

      // Handle when user stops sharing through browser UI
      stream.getTracks().forEach((track) => {
        track.onended = () => {
          console.log('Screen share ended by user');
          setIsScreenSharingActive(false);
          setScreenStream(null);
          setCaptureInfo(null);
        };
      });
    } catch (error) {
      console.error('Failed to acquire screen stream:', error);
      setIsScreenSharingActive(false);
      throw error;
    }
  }, [screenRecorder, isScreenSharingActive]);

  const startScreenRecording = useCallback(
    async (audioStream?: MediaStream) => {
      if (!screenRecorder || !isScreenSharingActive) {
        return;
      }
      const settings = getOptimalRecordingSettings();
      screenRecorder.startRecording(settings, audioStream);
    },
    [screenRecorder, isScreenSharingActive],
  );

  const stopScreenRecording = useCallback(async () => {
    if (!screenRecorder) {
      return;
    }

    try {
      await screenRecorder.stopRecording();
      console.log('Screen recording stopped');
      setIsScreenSharingActive(false);
      setScreenStream(null);
      setCaptureInfo(null);
    } catch (error) {
      console.error('Failed to stop screen recording:', error);
      throw error;
    }
  }, [screenRecorder]);

  const setChunkHandler = useCallback(
    (handler: ((chunk: Blob) => void) | undefined) => {
      if (screenRecorder) {
        screenRecorder.onscreenchunk = handler;
      }
    },
    [screenRecorder],
  );

  const captureFrame = useCallback(async () => {
    if (screenRecorder) {
      return screenRecorder.captureFrame();
    }
    return null;
  }, [screenRecorder]);

  const contextValue: ScreenRecorderContextType = {
    isScreenSharingActive,
    screenStream,
    captureInfo,
    acquireScreenStream,
    startScreenRecording,
    stopScreenRecording,
    setChunkHandler,
    captureFrame,
  };

  return (
    <ScreenRecorderContext.Provider value={contextValue}>
      {children}
    </ScreenRecorderContext.Provider>
  );
};
